import { getTsgToken, getFrameworkServiceUrl } from '@sparky/framework'
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import { getVarValue } from '../../common/utils/Sparky'
import { TSG_NONE } from '../constants'

import { FF_USE_LOCAL_SUBSCRIPTION_SERVICE } from '../../common/utils/FeatureFlags'
import {
  HASHES_URL,
  SUPPORT_ACCOUNTS,
  LICENSES_URL,
} from '../../common/utils/ServiceUrls'

export type AxiosRequestConfigWithTsg = AxiosRequestConfig & {
  tsg_id?: string | undefined
}
const includeClaims = getVarValue(FF_USE_LOCAL_SUBSCRIPTION_SERVICE) || false

const SUB_SVC_URL_KEY = 'subscription_service'
const API_GW_GS_URL_KEY = 'api_gateway_global_service'
const API_GW_URL_KEY = 'api_gateway'
const apiGwUrl = getFrameworkServiceUrl(API_GW_GS_URL_KEY) || getFrameworkServiceUrl(API_GW_URL_KEY) || getVarValue(API_GW_URL_KEY) || undefined
const SubscriptionAPI = axios.create({
  baseURL: getFrameworkServiceUrl(SUB_SVC_URL_KEY) || getVarValue(SUB_SVC_URL_KEY) || undefined,
  timeout: 300 * 1000
})

SubscriptionAPI.interceptors.request.use(async (config: AxiosRequestConfigWithTsg) => {
  const token = await getTsgToken(config?.tsg_id)
  config.headers = { ...config.headers, 'Authorization': `Bearer ${token}` }
  if (includeClaims) {
    config.headers.claims = token.split('.')[1]
  }
  return config
}, error => {
  return Promise.reject(error)
})

SubscriptionAPI.interceptors.response.use((response) => {
  return response
}, (error) => {
  const data = error.response?.data
  let errorMessage = data?.payload?.userMessage || data?.message || data?.error_message || data?.msg || error.message
  const errorObj = error.response?.data?.payload?.errors?.find((e: any) => Array.isArray(e.apps))
  const errorApps = errorObj?.apps.join(',')
  if (Boolean(errorApps)) {
    const applicationCode = error.response?.data?.application_code
    errorMessage = `${applicationCode} : ${errorApps} ${errorObj?.message}`
  }
  throw new Error(errorMessage)
})

const SubscriptionService = {
  hashes: {
    getOne: ({ tsg_id, hash, v }: { tsg_id?: string; hash: string, v?: string }) => {
      const customConfig: AxiosRequestConfigWithTsg = {
        tsg_id: TSG_NONE.id,
        params: {
          v
        }
      }
      //return Promise.resolve({ data: mockHashesResponse })
      return SubscriptionAPI.get(`${HASHES_URL}/${hash}`, customConfig) //Promise.resolve({ data: mockBackBoneHashResponse })
    }
  },
  activateInstance: {
    post: async (data: any, tsg_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      return SubscriptionAPI.post('/instances', data, { ...axiosObject })
    }
  },
  licenses: {
    get: async ({ support_account_ids, tsg_id, app_id }: { support_account_ids: string[] | number[], tsg_id?: string, app_id?: string }) => {
      const axiosObject: AxiosRequestConfigWithTsg = {
        params: {
          support_accounts: support_account_ids.join(','),
          ...(app_id && { app_id })
        },
        tsg_id: TSG_NONE.id,
        // tentatively use api-gw baseurl
        baseURL: apiGwUrl,
      }
      return SubscriptionAPI.get(`/subscription/v2${LICENSES_URL}`, axiosObject)
    },
    getOne: ({ tsg_id, license_id, params }: { tsg_id?: string; license_id: string, params?: { link_app_ids: string } }) => {
      const axiosObject: AxiosRequestConfigWithTsg = {
        tsg_id: TSG_NONE.id,
        params,
        baseURL: apiGwUrl,
      }
      return SubscriptionAPI.get(`/subscription/v2/${LICENSES_URL}/${license_id}`, axiosObject)
    }
  },
  activateZTPDevice: {
    post: async (data: any, tsg_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      return SubscriptionAPI.post('/ztp_device_activations', data, { ...axiosObject })
    }
  },
  validateSubDomain: {
    post: async (request: any, tsg_id: string, app_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      return SubscriptionAPI.post(`/validate/${app_id}/subdomain`, request, { ...axiosObject })
    }
  },
  instancesFromCSP: {
    get: async (app_id: string, tsg_id: string, csp_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id: TSG_NONE.id }
      return SubscriptionAPI.get(`${SUPPORT_ACCOUNTS}/${csp_id}/${app_id}`, axiosObject)
    }
  },
  deploymentProfiles: {
    get: async (tsg_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      return SubscriptionAPI.get('/profiles', axiosObject)
    },
    getForUser: async (tsg_id: string, user: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      const url = `/profiles?user=${user}`
      return SubscriptionAPI.get(url, axiosObject)
    },
    getForCSP: async (tsg_id: string, csp_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      const url = `/profiles?support_accounts=${csp_id}`
      return SubscriptionAPI.get(url, axiosObject)
    },
    getDP: async (tsg_id: string, dp_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      const url = `/profiles?deployment_profile_id=${dp_id}`
      return SubscriptionAPI.get(url, axiosObject)
    },
    post: async (data: any, tsg_id: string) => {
      const axiosObject: AxiosRequestConfigWithTsg = { tsg_id }
      return SubscriptionAPI.post('/profiles', data, { ...axiosObject })
    }
  },
  devices: {
    get: async ({ license_skus, tsg_id }: { license_skus: string, tsg_id?: string }) => {
      const axiosObject: AxiosRequestConfigWithTsg = {
        params: {
          device_skus: license_skus
        },
        tsg_id,
        // tentatively use api-gw baseurl
        baseURL: apiGwUrl,
      }
      return SubscriptionAPI.get('/subscription/v1/sku_compatible_devices', axiosObject)
    },
    licenses: async ({ serial_numbers, tsg_id }: { serial_numbers: string, tsg_id?: string }) => {
      const axiosObject: AxiosRequestConfigWithTsg = {
        params: {
          serial_numbers: serial_numbers
        },
        tsg_id,
        // tentatively use api-gw baseurl
        baseURL: apiGwUrl,
      }
      return SubscriptionAPI.get('/subscription/v1/csp_devices_licenses', axiosObject)
    }
  },
}

export default SubscriptionService
